import Dexie, { Table } from 'dexie';

export type UniversalWeightType = '1RM' | '5RM' | '5RM_1RIR';

export type MuscleGroup = 
  | 'Chest'
  | 'Back'
  | 'Lats'
  | 'Rhomboids'
  | 'Traps'
  | 'Shoulders'
  | 'Front Delts'
  | 'Side Delts'
  | 'Rear Delts'
  | 'Biceps'
  | 'Triceps'
  | 'Forearms'
  | 'Core'
  | 'Abs'
  | 'Obliques'
  | 'Quads'
  | 'Hamstrings'
  | 'Glutes'
  | 'Calves'
  | 'Cardiovascular'
  | 'Full Body';

export interface Exercise {
  id?: number;
  name: string;
  description?: string;
  category?: string;
  primaryMuscles?: MuscleGroup[];
  secondaryMuscles?: MuscleGroup[];
  universalWeight?: number;
  universalWeightType?: UniversalWeightType;
  universalWeightUpdatedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface TrainingDay {
  id?: number;
  name: string;
  exercises: TrainingDayExercise[];
  createdAt: Date;
  updatedAt: Date;
}

export interface TrainingDayExercise {
  id?: number;
  trainingDayId: number;
  exerciseId: number;
  weight: number;
  sets: SetTemplate[];
  order: number;
  discoveryMode: boolean;
}

export interface SetTemplate {
  targetReps: number;
  order: number;
}

export interface Routine {
  id?: number;
  name: string;
  trainingDayIds: number[];
  currentDayIndex: number;
  active?: boolean;
  archived?: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface WorkoutSession {
  id?: number;
  routineId: number;
  trainingDayId: number;
  startTime: Date;
  endTime?: Date;
  duration?: number;
  completed: boolean;
  currentExerciseIndex?: number;
  createdAt: Date;
}

export interface ExercisePerformance {
  id?: number;
  sessionId: number;
  exerciseId: number;
  weight: number;
  targetSets: SetTemplate[];
  finalSetActualReps: number;
  skipped: boolean;
  notes?: string;
  order: number;
}

export class WorkoutDatabase extends Dexie {
  exercises!: Table<Exercise>;
  trainingDays!: Table<TrainingDay>;
  trainingDayExercises!: Table<TrainingDayExercise>;
  routines!: Table<Routine>;
  workoutSessions!: Table<WorkoutSession>;
  exercisePerformances!: Table<ExercisePerformance>;

  constructor() {
    super('WorkoutTrackerDB');
    
    // Database schema version 1 - Initial structure
    // IMPORTANT: Only increment version when schema structure changes
    // Adding fields is safe, removing/renaming requires version bump + migration
    this.version(1).stores({
      exercises: '++id, name, category, createdAt',
      trainingDays: '++id, name, createdAt',
      trainingDayExercises: '++id, trainingDayId, exerciseId, order',
      routines: '++id, name, currentDayIndex, createdAt',
      workoutSessions: '++id, routineId, trainingDayId, startTime, completed',
      exercisePerformances: '++id, sessionId, exerciseId, skipped, order'
    });

    // Future schema changes should be added like this:
    // this.version(2).stores({
    //   // ... existing tables (must match version 1)
    //   newTable: '++id, field1, field2'
    // }).upgrade(tx => {
    //   // Migration logic here
    //   return tx.existingTable.toCollection().modify(item => {
    //     // Transform existing data
    //   });
    // });
  }
}

export const db = new WorkoutDatabase();